import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Accordion, AccordionTab } from "primereact/accordion";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useNegotiationsStore } from "../../stores/negotiationsStore";
import CustomButton from "../../components/Common/CustomButton/CustomButton";
import CreateNegotiationModal from "../../components/Common/CreateNegotiationModal/CreateNegotiationModal";
import bannerImage from "../../assets/images/banner.png";
import "./Dashboard.scss";

const Dashboard = () => {
	const navigate = useNavigate();
	const [showCreateModal, setShowCreateModal] = useState(false);

	const { negotiations, fetchNegotiations } = useNegotiationsStore();

	useEffect(() => {
		fetchNegotiations();
	}, [fetchNegotiations]);

	const handleCreateSuccess = () => {
		fetchNegotiations();
	};
	const actionsBodyTemplate = (rowData: any) => {
		const handleViewClick = () => {
			// it should be stored in negotiation store
			sessionStorage.setItem(
				"negotiation_workspace",
				JSON.stringify(rowData)
			);
			navigate(`/negotiation-packages/${rowData.negotiation_id}`);
		};

		return (
			<CustomButton
				label="View"
				variant="outlined"
				iconsOnly={false}
				onClick={handleViewClick}
			/>
		);
	};

	const totalNegotiations = negotiations?.length ?? 0;
	const preparingCount = negotiations?.filter((n: any) => n.status === "Preparing").length ?? 0;
	const newCount = negotiations?.filter((n: any) => n.status === "New").length ?? 0;
	const suppliersTotal = negotiations?.reduce((sum: number, n: any) => sum + (n.suppliers_involved || 0), 0) ?? 0;

	return (
		<div className="homepage">
			<div className="dashboard-banner">
				<img src={bannerImage} alt="Kearney banner" />
				<div className="dashboard-banner__content">
					<h1 className="dashboard-banner__title">NegotiationIQ</h1>
					<h2 className="dashboard-banner__subtitle">Negotiation Intelligence, Reimagined.</h2>
					<p className="dashboard-banner__copy">
						NegotiationIQ accelerates negotiation preparation by combining structured data,
						AI-driven analysis, and consulting-grade insights to support better outcomes.
					</p>
				</div>
			</div>

			<div className="dashboard-card-grid">
				<div className="dashboard-kpi">
					<span className="dashboard-kpi__label">Total Negotiations</span>
					<span className="dashboard-kpi__value">{totalNegotiations}</span>
				</div>
				<div className="dashboard-kpi">
					<span className="dashboard-kpi__label">Preparing</span>
					<span className="dashboard-kpi__value">{preparingCount}</span>
				</div>
				<div className="dashboard-kpi">
					<span className="dashboard-kpi__label">New</span>
					<span className="dashboard-kpi__value">{newCount}</span>
				</div>
				<div className="dashboard-kpi">
					<span className="dashboard-kpi__label">Suppliers Involved</span>
					<span className="dashboard-kpi__value">{suppliersTotal}</span>
				</div>
			</div>

			<div className="w-full">
				<Accordion className="mb-8">
					<AccordionTab
						header={
							<span className="flex items-center">
								<i className="pi pi-info-circle text-purple-600 mr-2"></i>
								Important Information
							</span>
						}
					>
						<div className="bg-purple-50 border border-purple-200 rounded-md p-3 mb-2">
							<h4 className="font-semibold mb-2 text-purple-800">
								Known Limitations
							</h4>
							<ul className="list-disc pl-4 mb-3 text-sm text-purple-700">
								<li>
									Chatbot may provide incomplete or inaccurate
									responses for complex or multi-step queries.
								</li>
								<li>
									Document ingestion is limited to specific
									file formats (XLSX, PDF, and DOCX) and may
									require manual uploads.
								</li>
								<li>
									Historical data extraction may miss certain
									fields or categories, please report any
									critical gaps.
								</li>
								<li>
									Feedback mechanism (thumbs-up/down) only
									captures binary input, without explanation.
								</li>
								<li>
								Data extracts of Kearney invoice spend data and
									Ariba tender data (via Bid Comparison Tool)
									have been loaded into the tool. Extracts
									from other systems will be added during
									subsequent releases.
								</li>
							</ul>
							<h4 className="font-semibold mb-2 text-purple-800">
								User Brief
							</h4>
							<p className="text-sm text-purple-700 mb-2">
								Focus your feedback on the accuracy of chatbot
								responses, usefulness of summaries, and whether
								the extracted data aligns with expectations in
								negotiation preparation. When cross-checking
								data, remember it may not match current live
								systems.
							</p>
						</div>
					</AccordionTab>
				</Accordion>

				<div className="flex justify-end my-4">
					<CustomButton
						label="Create Negotiation"
						icon="pi pi-plus"
						variant="primary"
						iconsOnly={false}
						onClick={() => setShowCreateModal(true)}
					/>
				</div>

				<DataTable
					value={negotiations}
					paginator
					rows={25}
					rowsPerPageOptions={[10, 25, 50]}
					tableStyle={{ minWidth: "50rem" }}
					emptyMessage="No negotiations found"
				>
					<Column field="name" header="Bundle Name"></Column>
					<Column field="status" header="Object Status"></Column>
					{/* <Column
						field="contact"
						header="Resposible Contact"
					></Column> */}
					<Column
						field="articles_count"
						header="No. of Articles"
					></Column>
					<Column
						field="bundle_value_updated"
						header="Bundle Value"
					></Column>
					<Column
						field="participating_countries"
						header="Participating Countries"
					></Column>
					<Column
						field="suppliers_involved"
						header="Suppliers Involved"
					></Column>
					<Column
						header="Actions"
						body={actionsBodyTemplate}
						style={{ minWidth: "8rem" }}
					/>
				</DataTable>
			</div>

			<CreateNegotiationModal
				visible={showCreateModal}
				onHide={() => setShowCreateModal(false)}
				onSuccess={handleCreateSuccess}
			/>
		</div>
	);
};

export default Dashboard;
